import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Bot, Sparkles, MessageCircle, Zap, Eye, Users, TrendingUp, Play } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const Demo = () => {
  const [scrollY, setScrollY] = useState(0);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const demoFeatures = [
    {
      icon: MessageCircle,
      title: "Real-Time Conversations",
      description: "Experience instant AI responses with contextual understanding",
      color: "from-blue-400 to-cyan-500"
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Sub-second response times for seamless customer experience",
      color: "from-violet-400 to-purple-500"
    },
    {
      icon: Eye,
      title: "Visual Interface",
      description: "Beautiful, customizable chat interface that matches your brand",
      color: "from-green-400 to-emerald-500"
    },
    {
      icon: Users,
      title: "Multi-User Support",
      description: "Handle multiple conversations simultaneously without lag",
      color: "from-amber-400 to-orange-500"
    }
  ];

  return (
    <div className="pt-16 bg-gray-950 overflow-hidden">
      <SEOHead 
        title="Demo - BrixBot AI Customer Service in Action" 
        description="See BrixBot AI chatbot in action. Try our interactive demo and experience real-time AI responses, context awareness, and intelligent customer service automation."
        isIndexable={true}
      />
      {/* Hero Section with Parallax */}
      <section className="relative py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-violet-950/30"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        ></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-violet-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 hover:bg-violet-600/30 mb-8 text-lg py-2 px-6">
            <Play className="w-5 h-5 mr-2" />
            Interactive Demo
          </Badge>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
            See BrixBot AI
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 animate-pulse">
              In Action
            </span>
          </h1>
          
          <p className="text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
            Experience how BrixBot handles real customer conversations with intelligent responses, 
            context awareness, and your brand voice.
          </p>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section className="py-32 bg-gray-900/50 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-500/5 to-purple-500/5"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-6">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Live Interactive Demo
                  </Badge>
                  <h2 className="text-4xl font-bold text-white mb-6">
                    Experience BrixBot in Action
                  </h2>
                  <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
                    Try our live chatbot demo below. Ask questions, test responses, and see how BrixBot can transform your customer interactions.
                  </p>
                </div>

                {/* Full-width Professional Chatbot Integration */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-violet-500/10 to-purple-500/10 rounded-2xl blur-xl"></div>
                  <div className="relative bg-gray-900/80 backdrop-blur-sm border border-violet-500/20 rounded-2xl p-1">
                    <iframe 
                      style={{width: "100%", height: "700px"}} 
                      src="https://mychatbot.brixbot.dev/embed/cme8c4nri0003no1kgv58612f"
                      className="border-0 rounded-xl w-full"
                      title="BrixBot Interactive Demo"
                      allow="microphone; camera; clipboard-read; clipboard-write"
                    />
                  </div>
                </div>

                {/* Professional Info Section */}
                <div className="mt-8 text-center">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                    <div className="bg-gray-800/50 rounded-xl p-6 border border-violet-500/20">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <MessageCircle className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-white font-semibold mb-2">Real-Time Responses</h3>
                      <p className="text-gray-400 text-sm">Instant AI-powered responses with context understanding</p>
                    </div>
                    
                    <div className="bg-gray-800/50 rounded-xl p-6 border border-violet-500/20">
                      <div className="w-12 h-12 bg-gradient-to-br from-violet-400 to-purple-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Zap className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-white font-semibold mb-2">AI-Powered Intelligence</h3>
                      <p className="text-gray-400 text-sm">Advanced machine learning for natural conversations</p>
                    </div>
                    
                    <div className="bg-gray-800/50 rounded-xl p-6 border border-violet-500/20">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Eye className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-white font-semibold mb-2">Fully Customizable</h3>
                      <p className="text-gray-400 text-sm">Tailor appearance and behavior to match your brand</p>
                    </div>
                  </div>
                </div>
              </div>
          </div>
        </div>
      </section>

      {/* Demo Features */}
      <section className="py-32 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-6">
              <Sparkles className="w-4 h-4 mr-2" />
              Demo Features
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Why Our Demo Stands Out
            </h2>
            <p className="text-xl text-gray-300">
              Experience the difference with our advanced AI technology
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {demoFeatures.map((feature, index) => (
              <Card key={index} className="bg-gray-900/50 border-violet-500/20 text-center backdrop-blur-sm hover:border-violet-400/40 transition-all duration-500 group">
                <CardHeader>
                  <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-white text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-r from-violet-900/30 to-purple-900/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-8 text-lg py-2 px-6">
            <Play className="w-5 h-5 mr-2" />
            Ready to Start?
          </Badge>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Build Your Own
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">
              AI Chatbot?
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            Start with just $200 down payment and transform your customer service today.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-12 py-8 text-2xl rounded-3xl shadow-2xl shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 transform hover:scale-105"
              >
                <Sparkles className="w-6 h-6 mr-3" />
                Get Started Now
              </Button>
            </Link>
            
            <Link to="/pricing">
              <Button 
                variant="outline" 
                size="lg" 
                className="border-violet-500/50 text-violet-300 hover:bg-violet-600/10 hover:border-violet-400 px-12 py-8 text-2xl rounded-3xl backdrop-blur-sm transition-all duration-300"
              >
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Demo;